import { SSEClient, type SseConnectionEvent } from './sse-client';
import type {
  LiveUpdateState,
  LiveUpdateTransport,
} from './live-update-transport';

/**
 * The SSE adapter behind `LiveUpdateTransport`: the only thing the connection-health monitor's
 * side of the app imports that knows the live updates are Server-Sent Events (D-137). When the
 * connection moves to Azure SignalR, this file is replaced and nothing that reads the interface
 * changes.
 *
 * It maps the SSE client's three raw events and nothing else: `open` is connected, `error` is
 * reconnecting (the client has scheduled its own retry), `closed` is disconnected (closed on
 * purpose, nothing retrying). It deliberately reads no status code, no retry timer and nothing
 * about whether the stream is anonymous: those are SSE facts the monitor must not come to depend
 * on (rule 1).
 */
export class SseLiveUpdateTransport implements LiveUpdateTransport {
  private _state: LiveUpdateState = 'disconnected';
  private readonly _listeners = new Set<(state: LiveUpdateState) => void>();

  constructor(private readonly client: SSEClient = SSEClient.getInstance()) {
    client.onConnectionEvent((event) => this.apply(event));
  }

  get state(): LiveUpdateState {
    return this._state;
  }

  onState(listener: (state: LiveUpdateState) => void): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  reconnectNow(): void {
    this.client.reconnectNow();
  }

  /** One raw event to its state; a repeat of the current state is not announced. */
  private apply(event: SseConnectionEvent): void {
    const next: LiveUpdateState =
      event === 'open'
        ? 'connected'
        : event === 'error'
          ? 'reconnecting'
          : 'disconnected';
    if (next === this._state) return;
    this._state = next;
    this._listeners.forEach((listener) => {
      try {
        listener(next);
      } catch (err) {
        console.warn('Live update state listener error', err);
      }
    });
  }
}
